import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Navigation, Clock, Anchor, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { spots } from '../utils/mockData';
import { motion } from 'framer-motion';
export function SpotDetailPage() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const spot = spots.find(s => s.id === id);
  if (!spot) return <div data-id="element-209">Spot not found</div>;
  return <div className="pb-24 md:pb-8" data-id="element-210">
      {/* Header Image */}
      <div className="relative h-64 md:h-80 w-full" data-id="element-211">
        <img src={spot.image} alt={spot.name} className="w-full h-full object-cover" data-id="element-212" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" data-id="element-213" />

        <button onClick={() => navigate(-1)} className="absolute top-4 left-4 p-2 bg-white/20 backdrop-blur-md rounded-full text-white hover:bg-white/30 transition-colors" data-id="element-214">
          <ArrowLeft size={24} data-id="element-215" />
        </button>

        <div className="absolute bottom-0 left-0 p-6 text-white" data-id="element-216">
          <h1 className="text-3xl font-bold mb-2" data-id="element-217">{spot.name}</h1>
          <div className="flex items-center space-x-4 text-sm font-medium" data-id="element-218">
            <span className="bg-teal-500/80 px-2 py-1 rounded-lg backdrop-blur-sm" data-id="element-219">
              {spot.distance} away
            </span>
            <div className="flex items-center" data-id="element-220">
              <span className="text-yellow-400 mr-1" data-id="element-221">★</span>
              {spot.rating}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 md:px-8 -mt-6 relative z-10" data-id="element-222">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-223">
          {/* Main Content */}
          <div className="md:col-span-2 space-y-6" data-id="element-224">
            <Card className="p-6" data-id="element-225">
              <h2 className="text-xl font-bold text-text mb-4" data-id="element-226">
                About this spot
              </h2>
              <p className="text-text-muted leading-relaxed mb-6" data-id="element-227">
                {spot.description}
              </p>

              <div className="grid grid-cols-2 gap-4" data-id="element-228">
                <div className="bg-gray-50 dark:bg-slate-800 p-4 rounded-xl" data-id="element-229">
                  <div className="flex items-center text-text-muted mb-2" data-id="element-230">
                    <Clock size={16} className="mr-2" data-id="element-231" />
                    <span className="text-xs uppercase tracking-wider font-semibold" data-id="element-232">
                      Best Time
                    </span>
                  </div>
                  <p className="font-medium text-text" data-id="element-233">{spot.bestTime}</p>
                </div>
                <div className="bg-gray-50 dark:bg-slate-800 p-4 rounded-xl" data-id="element-234">
                  <div className="flex items-center text-text-muted mb-2" data-id="element-235">
                    <Anchor size={16} className="mr-2" data-id="element-236" />
                    <span className="text-xs uppercase tracking-wider font-semibold" data-id="element-237">
                      Difficulty
                    </span>
                  </div>
                  <p className="font-medium text-text" data-id="element-238">{spot.difficulty}</p>
                </div>
              </div>
            </Card>

            <Card className="p-6" data-id="element-239">
              <h2 className="text-xl font-bold text-text mb-4" data-id="element-240">Fish Species</h2>
              <div className="flex flex-wrap gap-2" data-id="element-241">
                {spot.species.map(fish => <Badge key={fish} variant="accent" className="text-sm py-1 px-3" data-id="element-242">
                    {fish}
                  </Badge>)}
              </div>
            </Card>

            <Card className="p-6" data-id="element-243">
              <h2 className="text-xl font-bold text-text mb-4" data-id="element-244">Facilities</h2>
              <div className="flex flex-wrap gap-2" data-id="element-245">
                {spot.facilities.map(facility => <Badge key={facility} variant="outline" className="text-sm py-1 px-3" data-id="element-246">
                    {facility}
                  </Badge>)}
              </div>
            </Card>
          </div>

          {/* Sidebar Actions */}
          <div className="space-y-6" data-id="element-247">
            <Card className="p-6 sticky top-24" data-id="element-248">
              <h3 className="font-bold text-text mb-4" data-id="element-249">Actions</h3>
              <Button className="w-full mb-3" size="lg" data-id="element-250">
                <Navigation className="mr-2 h-5 w-5" data-id="element-251" />
                Get Directions
              </Button>
              <Button variant="secondary" className="w-full" data-id="element-252">
                Save Spot
              </Button>

              <div className="mt-6 pt-6 border-t border-border" data-id="element-253">
                <div className="flex items-start p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg" data-id="element-254">
                  <AlertCircle className="w-5 h-5 text-orange-500 mt-0.5 mr-3 flex-shrink-0" data-id="element-255" />
                  <div className="text-sm" data-id="element-256">
                    <p className="font-medium text-orange-700 dark:text-orange-300 mb-1" data-id="element-257">
                      Regulation Notice
                    </p>
                    <p className="text-orange-600 dark:text-orange-400" data-id="element-258">
                      Catch and release only for Trout during spawning season.
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>;
}